import { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Skeleton, Button } from "../components/ui"
import { Shell } from "../components/shell"
import { CardDialog } from "../components/card-dialog"
import { BoardSettingsDialog } from "../components/board-settings-dialog"
import { NewCardDialog } from "../components/new-card-dialog"
import { useBoardSocket } from "../hooks/useBoardSocket"
import { ChevronLeft, ChevronRight, Calendar, Lock, Settings } from "lucide-react"
import { LIMITS, getPlan } from "../lib/plans"

const PRIORITY_DOT: Record<string, string> = {
  urgent: "bg-red-500",
  high: "bg-orange-400",
  medium: "bg-yellow-400",
  low: "bg-green-400",
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]

type CardItem = {
  id: string
  title: string
  priority: string | null
  dueDate: string | null
  columnName: string
  boardId: string
  projectId: string
}

/** Calendar view of cards on a board. Shows cards on their due dates in a monthly grid. */
export default function CalendarPage() {
  const { workspaceId, projectId, boardId } = useParams({ from: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId/calendar" })
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()

  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth())
  const [openCardId, setOpenCardId] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [newCardOpen, setNewCardOpen] = useState(false)
  useBoardSocket(boardId)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (e.target as HTMLElement).isContentEditable) return
      if (e.key === "Escape") { setNewCardOpen(false); setOpenCardId(null); setSettingsOpen(false) }
      if (e.key === "/") { e.preventDefault(); document.querySelector<HTMLInputElement>("[data-global-search]")?.focus() }
      if (e.key === "n" || e.key === "N") { e.preventDefault(); setNewCardOpen(true) }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ role: string; plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })
  const canTimeline = LIMITS[getPlan(workspace?.plan)].timeline
  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin"

  const { data: board, isLoading } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () => fetcher<{ name: string; color: string | null; projectId: string; columns: { id: string; name: string; cards: any[] }[] }>(`/api/boards/${boardId}`),
    enabled: !!session,
  })

  const exportCSV = () => {
    const rows: string[][] = [["Title", "Column", "Priority", "Due Date"]]
    for (const col of board?.columns ?? []) {
      for (const c of col.cards) {
        rows.push([c.title, col.name, c.priority || "", c.dueDate ? new Date(c.dueDate).toLocaleDateString() : ""])
      }
    }
    const csv = rows.map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url; a.download = `${board?.name || "calendar"}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  if (!session) {
    navigate({ to: "/login" })
    return null
  }

  const cards: CardItem[] = board?.columns.flatMap((col) =>
    col.cards
      .filter((c) => c.dueDate)
      .map((c) => ({ id: c.id, title: c.title, priority: c.priority, dueDate: c.dueDate, columnName: col.name, boardId, projectId, }))
  ) ?? []

  const cardsByDate = cards.reduce<Record<string, CardItem[]>>((acc, card) => {
    if (!card.dueDate) return acc
    const key = new Date(card.dueDate).toISOString().split("T")[0]
    if (!acc[key]) acc[key] = []
    acc[key].push(card)
    return acc
  }, {})

  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const daysInPrev = new Date(year, month, 0).getDate()

  const cells: { date: Date; isCurrentMonth: boolean }[] = []
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month - 1, daysInPrev - i), isCurrentMonth: false })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), isCurrentMonth: true })
  }
  const remaining = 42 - cells.length
  for (let d = 1; d <= remaining; d++) {
    cells.push({ date: new Date(year, month + 1, d), isCurrentMonth: false })
  }

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11) } else setMonth(m => m - 1) }
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0) } else setMonth(m => m + 1) }

  const todayStr = today.toISOString().split("T")[0]

  return (
    <Shell
      title={board?.name || "Calendar"}
      back={{ to: "/workspaces/$workspaceId/projects/$projectId", label: "Project" }}
      boardColor={board?.color}
      actions={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCSV} title="Export as CSV">Export CSV</Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}><Settings className="mr-1 h-4 w-4" />Settings</Button>
          )}
        </div>
      }
      tabs={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm">
            <Link
              to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId"
              params={{ workspaceId, projectId, boardId }}
              className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              Board
            </Link>
            <Link
              to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/list"
              params={{ workspaceId, projectId, boardId }}
              className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              List
            </Link>
            <span className="rounded-md bg-primary/10 px-3 py-1.5 font-medium text-primary">Calendar</span>
            {canTimeline ? (
              <Link
                to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/timeline"
                params={{ workspaceId, projectId, boardId }}
                className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Timeline
              </Link>
            ) : (
              <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="flex items-center gap-1 rounded-md px-3 py-1.5 text-muted-foreground/50 hover:text-muted-foreground" title="Pro feature">
                Timeline <Lock className="h-3 w-3" />
              </Link>
            )}
          </div>
          <span className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">N</kbd> new card
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">/</kbd> search
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd> close
          </span>
          <div className="flex items-center gap-2">
            <button onClick={prevMonth} className="rounded-md p-1.5 hover:bg-muted">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[140px] text-center text-sm font-semibold">{MONTH_NAMES[month]} {year}</span>
            <button onClick={nextMonth} className="rounded-md p-1.5 hover:bg-muted">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()) }}
              className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted"
            >
              Today
            </button>
          </div>
        </div>
      }
    >
      <main className="flex flex-1 flex-col overflow-hidden p-4" style={board?.color ? { background: board.color } : undefined}>
        {isLoading ? (
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 42 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
          </div>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-border">
              {DAY_NAMES.map((d) => (
                <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-muted-foreground">
                  {d}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid flex-1 grid-cols-7 grid-rows-6 overflow-auto">
              {cells.map((cell, i) => {
                const key = cell.date.toISOString().split("T")[0]
                const cellCards = cardsByDate[key] || []
                const isToday = key === todayStr
                const isCurrentMonth = cell.isCurrentMonth

                return (
                  <div
                    key={i}
                    className={`min-h-[90px] border-b border-r border-border p-1.5 sm:min-h-[110px] lg:min-h-[130px] ${!isCurrentMonth ? "bg-muted/30" : ""}`}
                  >
                    <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium sm:h-7 sm:w-7 sm:text-sm ${
                      isToday ? "bg-primary text-primary-foreground" : isCurrentMonth ? "text-foreground" : "text-muted-foreground"
                    }`}>
                      {cell.date.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {cellCards.slice(0, 3).map((card) => (
                        <button
                          key={card.id}
                          onClick={() => setOpenCardId(card.id)}
                          className="flex w-full items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[11px] font-medium hover:bg-muted sm:text-xs"
                          title={card.title}
                        >
                          {card.priority && (
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${PRIORITY_DOT[card.priority] || "bg-muted-foreground"}`} />
                          )}
                          <span className="truncate">{card.title}</span>
                        </button>
                      ))}
                      {cellCards.length > 3 && (
                        <p className="px-1 text-[10px] text-muted-foreground sm:text-xs">+{cellCards.length - 3} more</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-3 flex items-center gap-4 rounded-md border border-border bg-card/80 p-2 text-xs text-muted-foreground backdrop-blur-sm">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> Urgent</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-400" /> High</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-yellow-400" /> Medium</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-400" /> Low</span>
          <span className="ml-auto flex items-center gap-1 text-muted-foreground/80"><Calendar className="h-3 w-3" /> Cards shown by due date</span>
        </div>
      </main>
      <CardDialog
        open={!!openCardId}
        onClose={() => setOpenCardId(null)}
        cardId={openCardId}
        boardId={boardId}
        workspaceId={workspaceId}
      />
      <BoardSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        boardId={boardId}
        workspaceId={workspaceId}
      />
      <NewCardDialog
        open={newCardOpen}
        onClose={() => setNewCardOpen(false)}
        boardId={boardId}
      />
    </Shell>
  )
}
